import React, { useState } from 'react';
import { Home, Eye, EyeOff, LogOut, FolderTree, Menu, X, DollarSign } from 'lucide-react';
import { useUser } from '../providers/user';
import { CURRENCIES, getCurrencyByCode } from '../utils/currency';

const Header = ({
  currentPage,
  setCurrentPage,
  showAmounts,
  setShowAmounts,
  setExpenses,
  currency,
  setCurrency,
  showCurrency,
  setShowCurrency
}) => {
  const { currentUser, logout } = useUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const currentCurrency = getCurrencyByCode(currency);

  const handleLogout = () => {
    logout();
    setExpenses([]);
    setIsMobileMenuOpen(false);
  };

  const handleNavigate = (page) => {
    setCurrentPage(page);
    setIsMobileMenuOpen(false);
  };

  const toggleShowAmounts = () => {
    setShowAmounts(!showAmounts);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6">
      <div className="flex justify-between items-center">
        {/* Left side: Avatar and Name */}
        <div className="flex items-center gap-3">
          {currentUser?.picture && (
            <img
              src={currentUser.picture}
              alt={currentUser.name}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-blue-500"
            />
          )}
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-gray-800">💰 MY Finance App</h1>
            <p className="text-sm md:text-base text-gray-600 hidden md:block">
              Welcome back, {currentUser?.name || 'User'}!
            </p>
            <p className="text-xs text-gray-600 md:hidden">
              {currentUser?.name || 'User'}
            </p>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex gap-3 flex-wrap">
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage('home')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${currentPage === 'home'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
                }`}
            >
              <Home size={20} /> Home
            </button>
            <button
              onClick={() => setCurrentPage('categories')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${currentPage === 'categories'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
                }`}
            >
              <FolderTree size={20} /> Categories
            </button>
          </div>
          <button
            onClick={() => setShowAmounts(!showAmounts)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {showAmounts ? <EyeOff size={20} /> : <Eye size={20} />}
            {showAmounts ? 'Hide' : 'Show'} Amounts
          </button>

          {/* Currency Dropdown */}
          <div className="flex items-center gap-2">
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
            >
              {CURRENCIES.map(curr => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol || curr.code} {curr.name}
                </option>
              ))}
            </select>
          </div>

          {/* Show Currency Toggle */}
          <button
            onClick={() => setShowCurrency(!showCurrency)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            title={showCurrency ? 'Hide currency symbols' : 'Show currency symbols'}
          >
            <DollarSign size={20} />
            {showCurrency ? 'Hide' : 'Show'} Currency
          </button>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
          >
            <LogOut size={20} /> Logout
          </button>
        </div>

        {/* Mobile Burger Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden mt-4 pt-4 border-t border-gray-200 space-y-2">
          <button
            onClick={() => handleNavigate('home')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${currentPage === 'home'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 hover:bg-gray-200'
              }`}
          >
            <Home size={20} /> Home
          </button>

          <button
            onClick={() => handleNavigate('categories')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${currentPage === 'categories'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 hover:bg-gray-200'
              }`}
          >
            <FolderTree size={20} /> Categories
          </button>

          <button
            onClick={toggleShowAmounts}
            className="w-full flex items-center gap-3 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-left"
          >
            {showAmounts ? <EyeOff size={20} /> : <Eye size={20} />}
            {showAmounts ? 'Hide' : 'Show'} Amounts
          </button>

          {/* Currency Dropdown - Mobile */}
          <div className="w-full">
            <label className="block text-xs text-gray-600 mb-1 px-1">Currency</label>
            <select
              value={currency}
              onChange={(e) => {
                setCurrency(e.target.value);
                setIsMobileMenuOpen(false);
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
            >
              {CURRENCIES.map(curr => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol || curr.code} {curr.name}
                </option>
              ))}
            </select>
          </div>

          {/* Show Currency Toggle - Mobile */}
          <button
            onClick={() => {
              setShowCurrency(!showCurrency);
              setIsMobileMenuOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-left"
          >
            <DollarSign size={20} />
            {showCurrency ? 'Hide' : 'Show'} Currency
          </button>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-left"
          >
            <LogOut size={20} /> Logout
          </button>
        </div>
      )}
    </div>
  );
};

export default Header;
